"use client"

import Logo from "@/../public/goodshift/logo.png"
import { useMediaQuery } from "@relume_io/relume-ui"
import { AnimatePresence, motion } from "framer-motion"
import Link from "next/link"
import { useRouter } from "next/navigation"
import React, { useEffect, useRef, useState } from "react"
import { RxChevronDown } from "react-icons/rx"
import { twJoin, twMerge } from "tailwind-merge"
import LinkAsButton from "../buttons/LinkAsButton"
import LinkHelper from "../helpers/LinkHelper"
import Image from "../images/Image"
import { bodySmallStyles } from "../text/BodySmall"

type NavLink = {
  url: string
  title: string
  subMenuLinks?: NavLink[]
}

type Props = {
  navLinks: NavLink[]
}

const textStyle = twMerge(bodySmallStyles, "font-normal")

export type RelumeNavbarProps = React.ComponentPropsWithoutRef<"section"> & Partial<Props>

const Navbar = (props: RelumeNavbarProps) => {
  const { navLinks } = {
    ...NavbarPages,
    ...props,
  }

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const isMobile = useMediaQuery("(max-width: 991px)")
  const isFirstRender = useRef(true)
  useEffect(() => {
    isFirstRender.current = false
  }, [])

  return (
    <section
      id="relume"
      className="relative z-10 flex w-full items-center bg-background-primary px-[1rem] lg:px-[1rem] max-w-7xl mx-auto mt-4 lg:mt-0">
      <div className="size-full lg:flex lg:items-center lg:justify-between">
        <div className="flex min-h-full items-center gap-4">
          <div className="flex-1 flex flex-col sm:flex-row lg:items-center h-full max-w-3xs max-h-3xs pt-2 sm:pt-0">
            <Link href="/" className="inline-flex h-full">
              <Image priority alt="GoodShift Logo" src={Logo.src} width={142} height={23} />
            </Link>
            <span className="pl-16 text-[9px] sm:pt-[3px] sm:px-2 sm:-mt-[16px] text-left sm:text-center font-bold sm:text-white sm:bg-red rounded-[11px] h-[18px] min-w-[63px] ml-[0.5rem] whitespace-nowrap">
              Not for profit
            </span>
          </div>
          <HamburgerMenu action={setIsMobileMenuOpen} isMobileMenuOpen={isMobileMenuOpen} />
          <LinkHelper href={["/goede-doelen", "/actie/", "/charity", "/action/"]} partial>
            {(active) => <>{!active && <GoedeDoelenenButton className="lg:hidden" />}</>}
          </LinkHelper>
        </div>
        <motion.div
          variants={{
            open: {
              height: "var(--height-open, auto)",
            },
            close: {
              height: "var(--height-closed, 0)",
            },
          }}
          initial="close"
          exit="close"
          animate={isMobileMenuOpen ? "open" : "close"}
          transition={{ duration: 0.4 }}
          className="overflow-hidden lg:overflow-visible px-[5%] lg:flex lg:items-center lg:px-0 lg:[--height-closed:auto] lg:[--height-open:auto] gap-2 xl:gap-6">
          {navLinks.map((navLink, index) =>
            navLink.subMenuLinks && navLink.subMenuLinks.length > 0 ? (
              <SubMenu key={index} navLink={navLink} isMobile={isMobile} setIsMobileMenuOpen={setIsMobileMenuOpen} />
            ) : (
              <NavbarLink setIsMobileMenuOpen={setIsMobileMenuOpen} key={index} href={navLink.url}>
                <span className={textStyle}>{navLink.title}</span>
              </NavbarLink>
            ),
          )}
          <LinkHelper href={["/goede-doelen", "/actie/", "/charity", "/action/"]} partial>
            {(active) => (
              <motion.div
                variants={{
                  open: {
                    width: "180px",
                    padding: "4px",
                  },
                  close: {
                    width: "0",
                    padding: "0px",
                  },
                }}
                initial={active ? "close" : "open"}
                className="overflow-hidden "
                transition={isFirstRender.current ? { duration: 0 } : { duration: 0.33 }}
                animate={active ? "close" : "open"}>
                <GoedeDoelenenButton className="hidden lg:flex" showText />
              </motion.div>
            )}
          </LinkHelper>
        </motion.div>
      </div>
    </section>
  )
}

const SubMenu: React.FC<{
  navLink: NavLink
  isMobile: boolean
  setIsMobileMenuOpen: React.Dispatch<React.SetStateAction<boolean>>
}> = ({ navLink, isMobile, setIsMobileMenuOpen }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  return (
    <div
      onMouseEnter={() => !isMobile && setIsDropdownOpen(true)}
      onMouseLeave={() => !isMobile && setIsDropdownOpen(false)}
      className="relative">
      <button
        className="hidden lg:flex w-full items-center justify-between gap-2 py-3 text-left text-md lg:flex-none lg:justify-start lg:px-4 lg:py-2 lg:text-base hover:text-purple "
        onClick={() => setIsDropdownOpen((prev) => !prev)}>
        <span className={textStyle}>{navLink.title} </span>
        <motion.span
          variants={{
            rotated: { rotate: 180 },
            initial: { rotate: 0 },
          }}
          animate={isDropdownOpen ? "rotated" : "initial"}
          transition={{ duration: 0.3 }}>
          <RxChevronDown />
        </motion.span>
      </button>
      {/* only visible on mobile */}
      <div className="lg:hidden">
        {navLink.subMenuLinks?.map((navLink, index) => (
          <NavbarLink key={index} href={navLink.url} setIsMobileMenuOpen={setIsMobileMenuOpen}>
            <span className={textStyle}>{navLink.title}</span>
          </NavbarLink>
        ))}
      </div>
      <div className="hidden lg:block">
        {isDropdownOpen && (
          <AnimatePresence>
            <motion.nav
              variants={{
                open: {
                  visibility: "visible",
                  opacity: "var(--opacity-open, 100%)",
                  y: 0,
                },
                close: {
                  visibility: "hidden",
                  opacity: "var(--opacity-close, 0)",
                  y: "var(--y-close, 0%)",
                },
              }}
              animate={isDropdownOpen ? "open" : "close"}
              initial="open"
              exit="close"
              transition={{ duration: 0.2 }}
              className="bg-white lg:shadow-2xl lg:absolute lg:right-0 lg:z-50 lg:border lg:border-border-primary lg:py-4 lg:px-8 lg:[--y-close:25%] lg:rounded-md">
              {navLink.subMenuLinks?.map((navLink, index) => (
                <NavbarLink setIsMobileMenuOpen={setIsMobileMenuOpen} key={index} href={navLink.url}>
                  <span className={twJoin(textStyle, "whitespace-nowrap")}>{navLink.title}</span>
                </NavbarLink>
              ))}
            </motion.nav>
          </AnimatePresence>
        )}
      </div>
    </div>
  )
}

export const NavbarPages: Props = {
  navLinks: [
    { title: "Home", url: "/" },
    { title: "Goede doelen", url: "/goede-doelen" },
    { title: "Acties", url: "/acties" },
    { title: "Giftcard", url: "/giftcard" },
    {
      title: "Over GoodShift",
      url: "/over-ons",
      subMenuLinks: [
        { title: "Voor organisaties", url: "/voor-organisaties" },
        { title: "Voor goede doelen", url: "/voor-goede-doelen" },
        { title: "Blog", url: "/blog" },
        { title: "Over ons", url: "/over-ons" },
        { title: "GoodShift punten", url: "/goodshift-punten" },
      ],
    },
  ],
}

const topLineVariants = {
  open: {
    translateY: 8,
    transition: { delay: 0.1 },
  },
  rotatePhase: {
    rotate: -45,
    transition: { delay: 0.2 },
  },
  closed: {
    translateY: 0,
    rotate: 0,
    transition: { duration: 0.2 },
  },
}

const middleLineVariants = {
  open: {
    width: 0,
    transition: { duration: 0.1 },
  },
  closed: {
    width: "1.5rem",
    transition: { delay: 0.3, duration: 0.2 },
  },
}

const bottomLineVariants = {
  open: {
    translateY: -8,
    transition: { delay: 0.1 },
  },
  rotatePhase: {
    rotate: 45,
    transition: { delay: 0.2 },
  },
  closed: {
    translateY: 0,
    rotate: 0,
    transition: { duration: 0.2 },
  },
}
const HamburgerMenu: React.FC<{ action: React.Dispatch<React.SetStateAction<boolean>>; isMobileMenuOpen: boolean }> = ({
  action,
  isMobileMenuOpen,
}) => {
  return (
    <button
      className="flex size-12 flex-col items-center justify-center lg:hidden"
      onClick={() => action((prev) => !prev)}>
      <motion.span
        className="my-[3px] h-0.5 w-6 bg-black"
        animate={isMobileMenuOpen ? ["open", "rotatePhase"] : "closed"}
        variants={topLineVariants}
      />
      <motion.span
        className="my-[3px] h-0.5 w-6 bg-black"
        animate={isMobileMenuOpen ? "open" : "closed"}
        variants={middleLineVariants}
      />
      <motion.span
        className="my-[3px] h-0.5 w-6 bg-black"
        animate={isMobileMenuOpen ? ["open", "rotatePhase"] : "closed"}
        variants={bottomLineVariants}
      />
    </button>
  )
}

const NavbarLink: React.FC<{
  href: string
  children: React.ReactNode
  className?: string
  setIsMobileMenuOpen: React.Dispatch<React.SetStateAction<boolean>>
}> = ({ href, children, className, setIsMobileMenuOpen }) => {
  const router = useRouter()

  const handleClick = (e: React.MouseEvent) => {
    setIsMobileMenuOpen(false)

    // Force refresh for goede-doelen to get new random data
    if (href === "/goede-doelen") {
      e.preventDefault()
      router.push(href)
      router.refresh()
    }
  }

  return (
    <LinkHelper href={href}>
      {(active) => (
        <Link
          onClick={handleClick}
          href={href}
          scroll={active ? false : true}
          data-active={active ? "true" : "false"}
          className={twMerge(
            `data-[active=true]:border-red border-transparent border-l-4 lg:border-l-0 lg:border-b-2 
              flex items-center
              pl-5 pr-2 lg:px-2 lg:pt-1 
              hover:text-purple 
              min-h-12 lg:min-h-[72px]`,
            className,
          )}>
          <span className={textStyle}>{children}</span>
        </Link>
      )}
    </LinkHelper>
  )
}

const GoedeDoelenenButton: React.FC<{ className?: string; showText?: boolean }> = ({ className, showText }) => {
  const router = useRouter()

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    router.push("/goede-doelen")
    router.refresh()
  }

  return (
    <LinkAsButton variant={"small"} href={"/goede-doelen"} onClick={handleClick} className={className}>
      <Image
        className={showText ? "w-6 h-6 mr-2" : "w-6 h-6"}
        src={"/goodshift/img/icons/hand_heart_white.svg"}
        width={24}
        height={24}
        alt=""
      />
      {showText && <span className="hidden lg:inline">Doneren</span>}
    </LinkAsButton>
  )
}

export default Navbar
